import React, { useMemo, useState } from 'react';
import { FaMap, FaProjectDiagram, FaRegShareSquare } from 'react-icons/fa';
import {
  ChatBubbleLeftEllipsisIcon,
  HomeIcon,
} from '@heroicons/react/24/solid';
import Link from 'next/link';
import { useParams, usePathname, useRouter } from 'next/navigation';

import { Fragment } from 'react';
import { Dialog, Popover, Portal, Transition } from '@headlessui/react';

import { createClient } from '../../lib/supabaseSetup/client';
import LoadingOverlay from '../global/loading/LoadingOverlay';
import { toast } from 'react-toastify';
import camelcaseKeys from 'camelcase-keys';
import { CareerMap } from '../../types';
import { PostgrestError, SupabaseClient, User } from '@supabase/supabase-js';
import { convertPostgresDate } from '../../lib/date';
import { createMarkdownPost } from '../../app/actions/createMarkdownPost';
// const mixpanelClient = getMixPanelClient();
import { shareMap } from '../../lib/share';

import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { cn } from '@/lib/utils';
import EmptyState from './UserMenuEmptyState';
import { contentAtom, pass1LearningPathAtom, pass2LearningPathAtom, mapDataAtom, mobileSidebar } from '@/jotaiStore';
import { jobTitleAtom } from '@/jotaiStore';
import { useAtom } from 'jotai';

const fetchUserMapsFromDbByUserID = async (
  user: User
): Promise<{ data: CareerMap[] | null; error: PostgrestError | null }> => {
  const SupabaseClient = createClient();

  //fetch all maps that match the user's id
  const { data, error } = await SupabaseClient.from('maps')
    .select('*')
    .eq('user_id', user.id)
    // .eq('user_id', '4caa6381-3d2d-4c57-8587-5e6979d95165')
    .order('updated_at', { ascending: true });
  if (error) {
    return { data: null, error };
  }
  return { data: camelcaseKeys(data), error };
};

/*
.##.....##..######..########.########..##.....##.########.##....##.##.....##
.##.....##.##....##.##.......##.....##.###...###.##.......###...##.##.....##
.##.....##.##.......##.......##.....##.####.####.##.......####..##.##.....##
.##.....##..######..######...########..##.###.##.######...##.##.##.##.....##
.##.....##.......##.##.......##...##...##.....##.##.......##..####.##.....##
.##.....##.##....##.##.......##....##..##.....##.##.......##...###.##.....##
..#######...######..########.##.....##.##.....##.########.##....##..#######.
*/

export const UserMenuHeader = ({ user }) => {
  const pathname = usePathname();
  const isOnIndexPage = pathname === '/';

  if (!user) return null;

  return (
    <div className="p-4 z-20 border-b border-shades-text/10 shadow-shades-baseDark/15 shadow-lg flex items-center justify-between">
      <p className=" font-medium   text-shades-text text-md">
        <span className="hidden sm:inline">Logged in as: </span>
        <span className="text-colors-green">{user.email}</span>
      </p>
      {!isOnIndexPage && (
        <Link
          href="/"
          className="flex items-center gap-x-2 text-shades-text/75 hover:text-colors-green"
        >
          <HomeIcon className="w-5 h-5 " />
        </Link>
      )}
    </div>
  );
};

export const SavedMapsList = ({ savedMaps, setShowUserMenu }) => {
  const [, setJobTitle] = useAtom(jobTitleAtom);
  const [, setMapData] = useAtom(mapDataAtom);
  const [, setPass1LearningPath] = useAtom(pass1LearningPathAtom);
  const [, setPass2LearningPath] = useAtom(pass2LearningPathAtom);
  const [, setContentText] = useAtom(contentAtom);
  const [, setShowMobileSidebar] = useAtom(mobileSidebar);
  if (!savedMaps?.length || savedMaps.length === 0) return null;

  return (
    <div className="p-4 pt-4 ">
      <div className="flex justify-between">
        <h3 className="relative text-sm font-semibold leading-6 text-shades-text">
          YOUR MAPS{' '}
          <span className="mr-px text-xs">(</span>
          {savedMaps.length}
          <span className="mx-px text-xs">)</span>
        </h3>
      </div>
      <ul role="list" className="mt-2 space-y-2">
        {savedMaps.toReversed().map((currMap) => (
          <li key={currMap.id} className="relative group ">
            <Link
              href={`/map/${currMap.slug}`}
              onClick={() => {
                setJobTitle(null);
                setMapData(null);
                setPass1LearningPath(null);
                setPass2LearningPath(null);
                setContentText('');
                setShowUserMenu(false);
                setShowMobileSidebar(false);
              }}
              className="flex flex-col p-2 px-4 rounded-lg hover:bg-shades-baseLighterist text-shades-text/75"
            >
              <time dateTime={currMap.updatedAt} className="block text-xs ">
                {convertPostgresDate(currMap.updatedAt)}
              </time>

              <p className="block text-sm font-semibold leading-6 truncate text-shades-text">
                {currMap.jobTitle}
                <span className="absolute inset-0" />
              </p>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const UserMenuFooter = ({
  user,
  setShowFeedbackModal,
  mapSlug,
  setIsLoading,
}) => {
  const SupabaseClient = createClient();

  if (!user) return null;

  return (
    <div className="z-20 flex justify-between items-center border-t border-shades-text/10 p-4 py-3">
      <div className="flex gap-2 items-center">
        {!!mapSlug && (
          <button
            className="flex items-center gap-2 px-4 @[350px]/sidebar:pr-3 rounded-lg bg-colors-lavender text-shades-base font-semibold text-[12px] @[350px]/sidebar:text-sm leading-0.5 hover:bg-colors-lavender/90 transition-colors h-6"
            onClick={shareMap}
          >
            SHARE MAP
          </button>
        )}
        {/* <button
          className="flex items-center gap-2 px-4 @[350px]/sidebar:pr-3 rounded-lg bg-colors-lavender text-shades-base font-semibold text-[12px] @[350px]/sidebar:text-sm leading-0.5 hover:bg-colors-lavender/90 transition-colors h-6"
          onClick={() => {p
            setShowFeedbackModal(true);
          }}
        >
          GIVE FEEDBACK
          <ChatBubbleLeftEllipsisIcon
            className="h-4 w-4 shrink-0"
            aria-hidden="true"
          />
        </button> */}
        {/* {user?.email === 'thepeterlittle@gmail.com' && (
          <button
            className="flex items-center gap-2 px-4 @[350px]/sidebar:pr-3 py-1 rounded-lg bg-colors-lavender text-shades-base font-semibold text-[12px] @[350px]/sidebar:text-sm hover:bg-colors-lavender/90 transition-colors h-6"
            onClick={() => {
              createMarkdownPost(mapSlug as string);
            }}
          >
            Create ReadMe
          </button>
        )} */}
      </div>
      <button
        type="button"
        className="text-sm font-semibold leading-6 text-shades-text hover:underline"
        onClick={async () => {
          setIsLoading(true);
          const { error } = await SupabaseClient.auth.signOut();
          window.location.reload();

          if (error) {
            toast.error('Error logging out. Please try again.', {
              toastId: 'LOGOUT_ERROR',
              theme: 'dark',
            });
            setIsLoading(false);
          }
        }}
      >
        Logout
      </button>
    </div>
  );
};

export const UserMenuMainContent = ({
  isLoading,
  savedMaps,
  user,
  setShowUserMenu,
}) => {
  return (
    <div className="h-full overflow-y-auto scrollbar bg-transparent scrollbar-thumb-shades-baseLighterist scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-track-transparent scrollbar-w-2 shadow-[inset_-2px_-2px_4px_rgba(0,0,0,0.05)]">
      <>
        {isLoading ||
          (savedMaps == null && <LoadingOverlay text="Loading Maps..." />)}
        {!savedMaps ||
          (savedMaps.length === 0 && (
            <div className="py-5">
              <EmptyState user={user} />
            </div>
          ))}
        {!!savedMaps?.length && savedMaps.length > 0 && (
          <SavedMapsList
            savedMaps={savedMaps}
            setShowUserMenu={setShowUserMenu}
          />
        )}
      </>
    </div>
  );
};

const UserMenu: React.FC<{
  user: User | null;
  showUserMenu: boolean;
  setShowUserMenu: (show: boolean) => void;
  children: React.ReactNode;
  panelStyles: string;
}> = ({ user, showUserMenu, setShowUserMenu, children, panelStyles }) => {
  const getUsersQuery = useSuspenseQuery(
    queryOptions({
      queryKey: ['users'],
      queryFn: async () => {
        if (user) {
          const test = await fetchUserMapsFromDbByUserID(user);
          return test;
        }
      },
    })
  );

  const [isLoading, setIsLoading] = useState(false);
  const SupabaseClient = createClient();
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const mapSlug = useParams().mapSlug;

  const isNotOnMapPage = useMemo(() => {
    return !mapSlug;
  }, [mapSlug]);

  const savedMaps = getUsersQuery.data?.data;

  return (
    <>
      <Popover className="">
        {children}

        {showUserMenu && (
          <>
            <Portal>
              <Popover.Overlay
                as="div"
                className="w-[100vw] h-[100vh] z-50 top-0 left-0 fixed"
              />
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  id="userMenu"
                  className={cn(
                    'absolute left-[calc(100vw-12px)] z-[100] mt-[35px] flex w-screen max-w-max -translate-x-full top-[40px] rounded-lg pr-4'
                  )}
                >
                  <div
                    // zIndex="popover"
                    className={cn(
                      'flex flex-col w-screen max-w-80 lg:max-w-md flex-auto overflow-hidden rounded-lg   backdrop-blur-md text-sm leading-6 shadow-md shadow-shades-baseDark/25 ring-1 ring-gray-900/5 max-h-[calc(100vh-150px)] border-shades-text/10 border-[1px]',
                      isNotOnMapPage ? 'bg-shades-base' : 'bg-shades-baseLight'
                    )}
                  >
                    <UserMenuHeader user={user} />
                    <UserMenuMainContent
                      isLoading={isLoading}
                      savedMaps={savedMaps}
                      user={user}
                      setShowUserMenu={setShowUserMenu}
                    />
                    <UserMenuFooter
                      user={user}
                      setShowFeedbackModal={setShowFeedbackModal}
                      mapSlug={mapSlug}
                      setIsLoading={setIsLoading}
                    />
                  </div>
                </Popover.Panel>
              </Transition>
              {/* </Dialog> */}
            </Portal>
          </>
        )}
      </Popover>
    </>
  );
};

export default UserMenu;
